/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import Cookies from 'js-cookie';
import { Box, Stack, MenuItem, TextField, CircularProgress } from '@mui/material';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

import SocialLoginModal from '../components/modal/SocialLoginModal';
import { googleAuth, appleAuth } from '../service/socialAuth';
import { updateUserPhone } from '../api/users/saveUsers';
import { eventsbyId } from '../api/events/eventsData';
import useFirebaseUser from '../hooks/useFirebaseUser';

const quantityFieldSx = {
  width: 90,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(36,34,88,0.25)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--teal-500) !important',
  },
  '& .MuiSelect-select': {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    letterSpacing: '0.16em',
    color: 'var(--navy-500)',
  },
};

export default function EventDetail() {
  const [event, setEvent] = React.useState({});
  const [eventTickets, setEventTickets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const { user } = useFirebaseUser();
  const uid = user?.uid;
  const [quantities, setQuantities] = React.useState({});
  const [amount, setAmount] = React.useState('');
  const [isTicketSelected, setIsTicketSelected] = React.useState(false);
  const [ticketsForPayment, setTicketsForPayment] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const eventId = localStorage.getItem('eventId');
      const res = await eventsbyId(eventId);
      if (res?.status === 200) {
        setEvent(res.data);
        if (res.data.eventType === 'Purchasable') {
          setEventTickets(res.data?.tickets || []);
        }
      }
      setLoading(false);
    })();
  }, []);

  const handleQuantityChange = (e, index) => {
    setQuantities((p) => ({ ...p, [index]: e.target.value }));
  };

  const handleAddToCart = () => {
    const selected = [];
    let total = 0;
    let finalArray = [];
    eventTickets.forEach((ticket, index) => {
      const q = quantities[index] || 0;
      if (q > 0) {
        total += ticket.ticketPrice * q;
        selected.push({
          ticketName: ticket?.ticketTitle,
          EventName: event?.eventTitle,
          amount: ticket?.ticketPrice,
          description: ticket?.ticketDesc,
          quantity: q,
        });
        finalArray = [...selected, { totalAmount: total }];
      }
    });
    setAmount(total);
    if (selected.length) {
      if (!isTicketSelected) toast.success('You are ready for tickets payment!');
      setIsTicketSelected(true);
      setTicketsForPayment(finalArray);
    } else if (uid) {
      setIsTicketSelected(false);
      toast.error('Please Select a Ticket Quantity!');
    }
  };

  const handleGoogle = async () => {
    try {
      const res = await googleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleApple = async () => {
    try {
      const res = await appleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleAccountCreated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) Cookies.set('customerStripeID', res.customerStripeID);
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  return (
    <EditorialPageShell title={event?.eventTitle || 'Event'} description={event?.description}>
      <PageHero
        kicker="Event  ·  Detail"
        meta={event?.eventDate ? `${event.eventDate}  ·  ${event.eventTime}` : 'Upcoming'}
        title={event?.eventTitle || 'An event at KBT'}
        subtitle={event?.description ? undefined : 'A gathering of the kehilla.'}
        image={event?.image || '/assets/images/kbt/siyum.jpg'}
        height="78vh"
      />

      {loading ? (
        <EditorialSection align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'var(--navy-500)' }} />
          </Box>
        </EditorialSection>
      ) : (
        <EditorialSection
          kicker={event?.eventType ? `${event.eventType} Event` : 'About'}
          title="About this gathering."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
              gap: { xs: 6, md: 10 },
              alignItems: 'start',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <Stack spacing={3}>
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--teal-500)',
                  }}
                >
                  Home  /  Event  /  {event?.eventTitle}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 17,
                    lineHeight: 1.75,
                    color: 'var(--text)',
                    opacity: 0.88,
                  }}
                >
                  {event?.description}
                </Box>
              </Stack>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 280, md: 460 },
                  background: 'var(--navy-700)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={event?.image}
                  alt={event?.eventTitle}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </motion.div>
          </Box>
        </EditorialSection>
      )}

      {!loading && eventTickets.length > 0 && (
        <EditorialSection
          background="var(--paper-warm)"
          kicker="Reserve"
          title={`${event?.eventTitle} ·`}
          titleAccent="tickets."
          intro="Select how many seats you need for each tier. We'll handle the rest at checkout."
        >
          <Box sx={{ maxWidth: 720, mx: 'auto' }}>
            <Stack spacing={0}>
              {eventTickets.map((ticket, index) => (
                <Box
                  key={index}
                  sx={{
                    py: 3,
                    borderTop: '1px solid rgba(36,34,88,0.15)',
                    ...(index === eventTickets.length - 1 && {
                      borderBottom: '1px solid rgba(36,34,88,0.15)',
                    }),
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 700,
                        fontSize: { xs: 20, md: 24 },
                        color: 'var(--navy-500)',
                        lineHeight: 1.2,
                      }}
                    >
                      {ticket.ticketTitle}
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        letterSpacing: '0.18em',
                        color: 'var(--teal-500)',
                      }}
                    >
                      ${ticket.ticketPrice}
                    </Box>
                    {ticket.ticketDesc && (
                      <Box
                        sx={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 15,
                          lineHeight: 1.6,
                          color: 'var(--text)',
                          opacity: 0.75,
                        }}
                      >
                        {ticket.ticketDesc}
                      </Box>
                    )}
                  </Stack>
                  <TextField
                    select
                    defaultValue="0"
                    onChange={(e) => handleQuantityChange(e, index)}
                    sx={quantityFieldSx}
                  >
                    {[...Array(10)].map((_, i) => (
                      <MenuItem key={i} value={i}>
                        {i}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ))}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 4 }}>
              <EditorialButton
                variant="secondary"
                onClick={() => {
                  handleAddToCart();
                  localStorage.setItem('amount', amount);
                  localStorage.setItem('paymentType', 'Event Ticket');
                  localStorage.setItem('paymentTypeName', event?.eventTitle);
                  localStorage.setItem('tickets', JSON.stringify(ticketsForPayment));
                  if (!uid) setOpen(true);
                }}
                href={isTicketSelected && amount !== '' && uid ? '/payment-checkout' : null}
              >
                Continue
              </EditorialButton>
            </Box>
          </Box>
        </EditorialSection>
      )}

      <SocialLoginModal
        open={open}
        handleClose={() => setOpen(false)}
        googleLogin={handleGoogle}
        appleLogin={handleApple}
        onAccountCreated={handleAccountCreated}
        requirePhoneVerification={true}
      />
    </EditorialPageShell>
  );
}
